import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import * as _ from 'lodash';
import classNames from 'classnames';
import styles from '../../styles/views/stages/Stages.scss';
import {
  __,
  setTableParams,
  dateFrom,
  getLocalizedListValue,
} from '../../core/utils';
import BaseTable from 'react-base-table';
import AutoSizer from 'react-virtualized-auto-sizer';
import { withRouter } from 'react-router';
import ExpandIcon from './helpers/ExpandIcon';
import UncheckedIcon from '../../assets/images/unchecked-15x15.svg';
import NeutralIcon from '../../assets/images/neutral-15x15.svg';
import CheckedIcon from '../../assets/images/checked-15x15.svg';
import Tooltip from '../../components/Tooltip';
import {
  readProjectPerformance,
  readStage,
} from '../../redux/actions/table/stages';
import ArrowDoubleRightSmallIcon from '../../assets/images/arrow-double-right-small-15x15.svg';
import { readSubstage } from '../../redux/actions/table/substages';
import Dropdown from '../../components/Dropdown';
import ArrowDownSmallIcon from '../../assets/images/arrow-down-small-15x15.svg';
import TaskStatusIcon from '../../assets/images/task-status-15x15.svg';
import NumericInput, { formatNumber } from './helpers/NumericInput';
import moment from 'moment';
import WarningIcon from '../../assets/images/tender-icon-warning-16x16.svg';
import ColumnSelectorIcon from '../../assets/images/column-selector-16x16.svg';
import Button from '../../components/Button';
import ButtonGroup from '../../components/ButtonGroup';
import { hideDropdown } from '../../redux/actions/general/dropdown';

// import Row from './helpers/Row';

const formatBackendDate = (date) => {
  return moment(date, 'DD.MM.YYYY', true).format('YYYY-MM-DD');
};

const schedule_bar_colors = {
  green: '#39B54A',
  red: '#E53935',
  yellow: '#FBC02D',
  gray: '#E6E7EB',
  default: '#E6E7EB',
};

const resolveScheduleBarColor = (raw) => {
  if (raw == null) return null;

  const value = String(raw).trim();
  if (!value) return null;

  const normalized = value.toLowerCase();
  if (schedule_bar_colors[normalized]) return schedule_bar_colors[normalized];

  return schedule_bar_colors.default;
};

const formatPercentValue = (value) => `${_.round(Number(value) || 0, 2)}%`;

const getRowName = (row, languageId) =>
  _.isString(row?.name) ? row.name : _.get(row?.name, languageId);

const parseSignedNumericValue = (raw) => {
  if (_.isNil(raw)) return 0;
  if (_.isNumber(raw)) return _.isFinite(raw) ? raw : 0;

  const matches = String(raw).match(/[+-]?\d*\.?\d+/g);

  if (!matches) return 0;

  return _.sumBy(matches, (match) => Number(match) || 0);
};

const StagesTable = ({
  table,
  match,
  onExpandedRowsChange,
  focusedId,
  onFocusChange,
  expandedRows,
  listProjectSubstages,
  data,
  languageId,
  isProjectAdministrator,
  disabledColumns,
  dropdownName,
  onDisabledColumnsChange,
  onStatusChange,
  onSubstageUpdate,
}) => {
  const myRef = useRef();

  const stage_statuses = [
    {
      value: 'not-started',
      label: __('stages.status.not-started'),
      color: '#9C27B0',
    },
    {
      value: 'started',
      label: __('stages.status.started'),
      color: '#5777FC',
    },
    {
      value: 'finished',
      label: __('stages.status.finished'),
      color: '#ADD42B',
    },
    {
      value: 'closed',
      label: __('stages.status.closed'),
      color: '#009688',
    },
    {
      value: 'reopened',
      label: __('stages.status.reopened'),
      color: '#303F9F',
    },
    {
      value: 'canceled',
      label: __('stages.status.canceled'),
      color: '#CCCDD1',
    },
  ];

  //const [focusedContextMenuRow, setFocusedContextMenuRow] = useState({});

  const [checked, setChecked] = useState([]);
  const [dateStartColumn, setDateStartColumn] = useState({});
  const [projectPerformance, setProjectPerformance] = useState({});
  const [tempDisabledColumns, setTempDisabledColumns] = useState(
    disabledColumns || []
  );
  const projectId = _.get(match, 'params.project');

  useEffect(() => {
    setTempDisabledColumns(disabledColumns || []);
  }, [disabledColumns]);

  useEffect(() => {
    if (dropdownName === 'column_selector') {
      setTempDisabledColumns(disabledColumns || []);
    }
  }, [disabledColumns, dropdownName]);

  useEffect(() => {
    if (!projectId) return;

    readProjectPerformance(projectId).then(({ response }) => {
      setProjectPerformance(response.data || {});
    });
  }, [projectId]);

  const getDeepIds = useCallback((rows) => {
    const ids = [];

    const process = (rows) =>
      _.each(rows, (row) => {
        row.id != 'UN' && _.isEmpty(row.children) && ids.push(row.id);

        !_.isEmpty(row.children) && process(row.children);
      });

    process(rows);

    return ids;
  }, []);

  const final_data = useMemo(() => {
    const rows = data;

    const process = (rows) =>
      _.map(rows, (row) => {
        let is_checked = false;

        if (_.isEmpty(row.children)) {
          if (_.includes(checked, row.id)) {
            is_checked = true;
          }
        } else {
          const child_ids = getDeepIds(row.children);
          const checked_child_ids = _.intersection(checked, child_ids);

          if (checked_child_ids.length != 0) {
            if (child_ids.length == checked_child_ids.length) {
              is_checked = true;
            } else {
              is_checked = 'neutral';
            }
          }
        }

        return {
          ...row,
          checked: is_checked,
          children: _.isEmpty(row.children) ? undefined : process(row.children),
        };
      });

    return process(rows);
  }, [data, checked]);

  const data_ids = useMemo(() => getDeepIds(final_data), [final_data]);

  const _handleCheckbox = useCallback(
    (id) => {
      const target_ids = _.filter(data_ids, (child_id) =>
        _.startsWith(child_id, id)
      );

      const intersected = _.intersection(target_ids, checked).length;

      if (intersected == 0 || intersected != target_ids.length) {
        setChecked(_.union(checked, target_ids)); //select all
      } else {
        setChecked(_.difference(checked, target_ids)); //select none
      }
    },
    [data_ids, checked]
  );

  const _handleMasterExpand = (expanded_rows, unallocated_collapsed) => {
    onExpandedRowsChange(expanded_rows); //set expanded rows in local state
    myRef?.current?.setExpandedRowKeys(expanded_rows); //set expanded rows in dependency state
  };

  const headerRenderer = ({ headerIndex, cells, columns }) => {
    if (headerIndex === 0) {
      return _.map(columns, (column, i) => {
        if (column.key === 'name') {
          const expandableRows = { 2: [], 3: [], 4: [] };

          const collectExpandableRows = (rows) => {
            _.each(rows, (row) => {
              if (_.isEmpty(row.children)) return;

              const controlLevel = row.level + 2;

              if (controlLevel <= 4) {
                expandableRows[controlLevel].push(row.id);
              }

              collectExpandableRows(row.children);
            });
          };

          collectExpandableRows(data);

          const levels = {
            2: expandableRows[2],
            3: _.union(expandableRows[2], expandableRows[3]),
            4: _.union(
              expandableRows[2],
              expandableRows[3],
              expandableRows[4]
            ),
          };

          let active = {};

          for (let i = 2; i <= 4; i++) {
            active[i] =
              !_.isEmpty(expandableRows[i]) &&
              _.every(levels[i], (id) => _.includes(expandedRows, id));
          }

          const levelTooltips = {
            2: 'Show substage level',
            3: 'Show task-group level',
            4: 'Show nested task-group level',
          };

          return React.cloneElement(
            cells[i],
            null,
            <>
              <div className='BaseTable__header-cell-text'>
                {checked.length === data_ids.length && checked.length !== 0 && (
                  <CheckedIcon
                    className={classNames(styles.checked, styles.master)}
                    onClick={_handleMasterCheckbox}
                  />
                )}
                {checked.length === 0 && (
                  <UncheckedIcon
                    className={classNames(
                      styles.unchecked,
                      styles.master,
                      checked.length > 0 && styles.visible
                    )}
                    onClick={_handleMasterCheckbox}
                  />
                )}
                {checked.length !== data_ids.length && checked.length !== 0 && (
                  <NeutralIcon
                    className={classNames(styles.neutral, styles.master)}
                    onClick={_handleMasterCheckbox}
                  />
                )}
              </div>
              <div className={styles.masterActions}>
                <Tooltip text='Show stage level'>
                  <span
                    className={classNames(styles.active)}
                    onClick={() => _handleMasterExpand([], true, 1)}
                  >
                    1
                  </span>
                </Tooltip>
                {_.map([2, 3, 4], (level) => (
                  <Tooltip
                    key={level}
                    text={
                      _.isEmpty(expandableRows[level])
                        ? ''
                        : levelTooltips[level]
                    }
                  >
                    <span
                      className={classNames(
                        active[level] && styles.active,
                        _.isEmpty(expandableRows[level]) && styles.disabled
                      )}
                      onClick={() => {
                        if (_.isEmpty(expandableRows[level])) return;
                        _handleMasterExpand(levels[level], true, level);
                      }}
                    >
                      {level}
                    </span>
                  </Tooltip>
                ))}
              </div>
              <div className='BaseTable__header-cell-text'>Title</div>
            </>
          );
        } else {
          return React.cloneElement(cells[i], null);
        }
      });
    } else {
      return _.map(columns, (column, i) => {
        let children = _.filter(cells[i].props.children, _.identity);

        let value = '';

        children[0] = value;

        if (column.key == 'name') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {__('stages.sum')}{' '}
            </div>
          );
        }

        const minStartDateObject = _.minBy(data, (group) => group?.date?.start);
        const maxEndDateObject = _.maxBy(data, (group) => group?.date?.end);

        const start = _.get(minStartDateObject, 'date.start');
        const end = _.get(maxEndDateObject, 'date.end');

        const duration =
          moment(end)
            .startOf('day')
            .diff(moment(start).startOf('day'), 'days') + 1;

        const elapsed = start
          ? moment().startOf('day').diff(moment(start).startOf('day'), 'days')
          : 0;

        const elapsed_percent =
          duration > 0 ? _.round((elapsed / duration) * 100, 2) : 0;

        const budget = _.sumBy(data, (group) => group?.time_log.planned_time);

        const child_estimations_variance_total = _.sumBy(data, (group) =>
          parseSignedNumericValue(group?.time_log?.child_estimations_variance)
        );

        const logged = _.sumBy(data, (group) => group?.time_log.logged_time);

        const logged_percent =
          budget > 0 ? _.round((logged / budget) * 100, 2) : 0;

        const remaining_budget_logged_time = budget - logged;

        const completed_hours = _.sumBy(
          data,
          (group) => group?.time_log?.completed_hours
        );

        const completed_progress = budget > 0 ? completed_hours / budget : 0; // to do - check

        const pace_gap = completed_progress - elapsed_percent;

        const remaining_time = duration - elapsed;

        const efficiency_gap =
          completed_hours && completed_hours !== 0
            ? logged / completed_hours
            : 0;

        if (column.key == 'date.start') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {dateFrom(_.get(minStartDateObject, 'date.start'), true) || ''}
            </div>
          );
        }

        if (column.key == 'date.end') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {dateFrom(maxEndDateObject?.date.end, true) || ''}
            </div>
          );
        }

        if (column.key == 'date.duration') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(duration)}
            </div>
          );
        }

        if (column.key == 'schedule.elapsed_time') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(elapsed)}
            </div>
          );
        }

        if (column.key == 'schedule.elapsed_percent') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(elapsed_percent))}
            </div>
          );
        }

        if (column.key == 'schedule.pace_gap') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(pace_gap))}
            </div>
          );
        }

        if (column.key == 'schedule.remaining_time') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(remaining_time)}
            </div>
          );
        }

        // budget
        if (column.key == 'time_log.planned_time') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(budget))}
            </div>
          );
        }

        if (column.key == 'time_log.subtasks_planned_time') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(child_estimations_variance_total))}
            </div>
          );
        }

        if (column.key == 'time_log.logged_time') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(logged)}
            </div>
          );
        }

        if (column.key == 'time_log.logged_percent') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(logged_percent))}
            </div>
          );
        }

        if (column.key == 'time_log.efficiency_gap') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(efficiency_gap))}
            </div>
          );
        }

        if (column.key == 'time_log.remaining_time') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(remaining_budget_logged_time))}
            </div>
          );
        }

        if (column.key == 'time_log.completed_progress') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(completed_progress))}
            </div>
          );
        }

        if (column.key == 'time_log.completed_hours') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(Math.round(completed_hours))}
            </div>
          );
        }

        // header calculation coming from BE for the next five columns

        if (column.key == 'schedule.schedule_bar_color') {
          const color = resolveScheduleBarColor(
            projectPerformance?.schedule_rag
          );
          const ragPace = projectPerformance?.performance?.rag_pace || {};
          const scheduleTooltip = (
            <div className={styles.colorTooltip}>
              <div className={styles.colorTooltipRow}>
                <span>{__('stages.schedule_bar_color.tooltip.pace_gap')}</span>
                <strong>{formatPercentValue(ragPace?.pace_gap)}</strong>
              </div>
              <div className={styles.colorTooltipRow}>
                <span>
                  {__('stages.schedule_bar_color.tooltip.completed_percent')}
                </span>
                <strong>
                  {formatPercentValue(ragPace?.completed_percent)}
                </strong>
              </div>
              <div className={styles.colorTooltipRow}>
                <span>
                  {__('stages.schedule_bar_color.tooltip.elapsed_percent')}
                </span>
                <strong>{formatPercentValue(ragPace?.elapsed_percent)}</strong>
              </div>
            </div>
          );

          value = color ? (
            <Tooltip text={scheduleTooltip} html>
              <div className={styles.colorDotCell}>
                <span
                  className={styles.colorBall}
                  style={{ backgroundColor: color }}
                />
              </div>
            </Tooltip>
          ) : (
            ''
          );
        }

        if (column.key == 'time_log.time_log_color') {
          const color = resolveScheduleBarColor(
            projectPerformance?.time_log_rag
          );
          const ragEfficiency =
            projectPerformance?.performance?.rag_efficiency || {};
          const timeLogTooltip = (
            <div className={styles.colorTooltip}>
              <div className={styles.colorTooltipRow}>
                <span>{__('stages.time_log_color.tooltip.efficiency')}</span>
                <strong>{formatPercentValue(ragEfficiency?.efficiency)}</strong>
              </div>
              <div className={styles.colorTooltipRow}>
                <span>
                  {__('stages.time_log_color.tooltip.completed_percent')}
                </span>
                <strong>
                  {formatPercentValue(ragEfficiency?.completed_percent)}
                </strong>
              </div>
              <div className={styles.colorTooltipRow}>
                <span>
                  {__('stages.time_log_color.tooltip.logged_percent')}
                </span>
                <strong>
                  {formatPercentValue(ragEfficiency?.logged_percent)}
                </strong>
              </div>
            </div>
          );

          value = color ? (
            <Tooltip text={timeLogTooltip} html>
              <div className={styles.colorDotCell}>
                <span
                  className={styles.colorBall}
                  style={{ backgroundColor: color }}
                />
              </div>
            </Tooltip>
          ) : (
            ''
          );
        }

        if (column.key == 'performance.forecast_d') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(projectPerformance?.performance?.forecast_d)}
            </div>
          );
        }

        if (column.key == 'performance.forecast_h') {
          value = (
            <div className='BaseTable__header-cell-text'>
              {formatNumber(projectPerformance?.performance?.forecast_h)}
            </div>
          );
        }

        if (column.key == 'performance.color') {
          const color = resolveScheduleBarColor(
            projectPerformance?.performance?.color
          );
          const scheduleColor = resolveScheduleBarColor(
            projectPerformance?.schedule_rag
          );
          const timeLogColor = resolveScheduleBarColor(
            projectPerformance?.time_log_rag
          );
          const performanceTooltip = (
            <div className={styles.colorTooltip}>
              <div className={styles.colorTooltipRow}>
                <span>{__('stages.performance_color.tooltip.schedule')}</span>
                <strong className={styles.colorTooltipDotValue}>
                  <span
                    className={styles.colorBall}
                    style={{ backgroundColor: scheduleColor }}
                  />
                </strong>
              </div>
              <div className={styles.colorTooltipRow}>
                <span>{__('stages.performance_color.tooltip.time_log')}</span>
                <strong className={styles.colorTooltipDotValue}>
                  <span
                    className={styles.colorBall}
                    style={{ backgroundColor: timeLogColor }}
                  />
                </strong>
              </div>
            </div>
          );

          value = color ? (
            <Tooltip text={performanceTooltip} html>
              <div className={styles.colorDotCell}>
                <span
                  className={styles.colorBall}
                  style={{ backgroundColor: color }}
                />
              </div>
            </Tooltip>
          ) : (
            ''
          );
        }

        children[column.frozen ? 1 : 0] = value;

        return React.cloneElement(cells[i], null, children);
      });
    }
  };

  const getSubstageId = (id) =>
    typeof id === 'string' ? id.split('-').pop() : id;

  const _toggleColumn = useCallback((column) => {
    setTempDisabledColumns((current) => _.xor(current, [column]));
  }, []);

  const columns = [
    // {
    //   key: 'order',
    //   name: '#',
    //   required: true,
    //   default: true,
    //   width: 50,
    // },
    {
      key: 'name',
      name: __('Name'),
      required: true,
      width: 260,
      minWidth: 260,
      resizable: true,
      sortable: false,
      frozen: 'left',
      cellRenderer: ({ rowData }) => (
        <>
          {rowData?.checked === true && (
            <CheckedIcon
              className={styles.checked}
              onClick={() => _handleCheckbox(rowData?.id)}
            />
          )}
          {rowData?.checked === false && (
            <UncheckedIcon
              className={classNames(
                styles.unchecked,
                checked.length > 0 && styles.visible
              )}
              onClick={() => _handleCheckbox(rowData?.id)}
            />
          )}
          {rowData?.checked === 'neutral' && (
            <NeutralIcon
              className={styles.neutral}
              onClick={() => _handleCheckbox(rowData?.id)}
            />
          )}

          <div className={styles.name}>
            <span>{rowData?.code}</span>
            <input
              className={styles.input}
              type='text'
              disabled
              readOnly={!isProjectAdministrator} // only PA can edit
              onBlur={({ currentTarget }) => {}}
              key={rowData?.id + getRowName(rowData, languageId)}
              defaultValue={getRowName(rowData, languageId)}
              // defaultValue={rowData?.name?.[languageId] ?? ''}
              // defaultValue={
              //   typeof rowData?.name?.[languageId] === 'string'
              //     ? rowData.name[languageId]
              //     : ''
              // }
              maxLength={70}
            />
            {/* {rowData.level == 0 && (
              <span
                className={styles.readStage}
                onClick={() => {
                  readStage(rowData.id).then(({ response }) => {
                    setTableParams('stages', {
                      flyout: response.data,
                      clicked_stage_row: rowData.id,
                    });
                  });
                }}
              >
                <ArrowDoubleRightSmallIcon />
              </span>
            )} */}

            {rowData.level <= 1 && (
              <span
                className={styles.readStage}
                onClick={() => {
                  rowData.level == 0
                    ? readStage(rowData.id).then(({ response }) => {
                        setTableParams('stages', {
                          flyout: response.data,
                          clicked_stage_row: rowData.id,
                          clicked_substage_row: null,
                        });
                      })
                    : readSubstage(rowData.ownId).then(({ response }) => {
                        setTableParams('stages', {
                          substage_flyout: response.data,
                          clicked_substage_row: rowData.id,
                          clicked_stage_row: null,
                        });
                      });
                }}
              >
                <ArrowDoubleRightSmallIcon />
              </span>
            )}
          </div>
        </>
      ),
    },
    {
      key: 'task_id',
      name: __('task_id'),
      required: true,
      width: 95,
      minWidth: 95,
      resizable: true,
      sortable: false,
      frozen: 'left',
      cellRenderer: ({ rowData }) => rowData?.task_id || '',
    },
    {
      key: 'date.start',
      title: __('stages.date.start'),
      align: 'right',
      width: 105,
      resizable: true,
      sortable: false,
      cellRenderer: ({
        rowData: { name, date, id, level, time_log, status },
      }) => {
        const substage_id = _.last(_.split(id, '-'));
        const disabled = status === 'canceled';

        return (
          <NumericInput
            key={id + date.start}
            onBlur={(startDate) => {
              if (disabled) return;
              return onSubstageUpdate(substage_id, {
                name,
                date: {
                  ...date,
                  start: formatBackendDate(startDate),
                  duration: date?.duration + 'd',
                },
                time_log: {
                  ...time_log,
                  planned_time: time_log?.planned_time + ' h',
                },
              });
            }}
            dateFormat
            readOnly={level !== 1 || disabled}
            defaultValue={dateFrom(date.start, true)}
          />
        );
      },
    },
    {
      key: 'date.end',
      title: __('stages.date.end'),
      align: 'right',
      width: 105,
      resizable: true,
      sortable: false,
      cellRenderer: ({
        rowData: { name, date, id, level, time_log, status },
      }) => {
        const substage_id = _.last(_.split(id, '-'));
        const disabled = status === 'canceled';
        const deadlineLocked = Number(date?.deadline) === 1;

        return (
          <NumericInput
            key={id + date.end}
            onBlur={(endDate) => {
              if (disabled || deadlineLocked) return;
              return onSubstageUpdate(substage_id, {
                name,
                date: {
                  ...date,
                  end: formatBackendDate(endDate),
                  duration: date?.duration + 'd',
                },
                time_log: {
                  ...time_log,
                  planned_time: time_log?.planned_time + ' h',
                },
              });
            }}
            dateFormat
            readOnly={level !== 1 || disabled || deadlineLocked}
            defaultValue={dateFrom(date.end, true)}
          />
        );
      },
    },
    {
      key: 'date.deadline_date',
      title: __('deadline'),
      align: 'center',
      width: 75,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const deadline_date = rowData?.date?.deadline_date;
        if (!deadline_date) return null;
        const formattedDate = dateFrom(deadline_date, true);
        return (
          <div className={styles.deadlineCell}>
            <Tooltip text={formattedDate}>
              <span className={styles.deadlineIconBox}>
                <WarningIcon className={styles.deadlineIcon} />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: 'date.duration',
      title: __('stages.date.duration'),
      align: 'right',
      // width: 200,
      width: 91,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const {
          status,
          id,
          level,
          date,
          name,
          description,
          time_log,
          date: { duration },
          ...otherProperties
        } = rowData;

        const substage_id = _.last(_.split(id, '-'));
        const disabled = status === 'canceled';
        const deadlineLocked = Number(date?.deadline) === 1;

        return (
          <NumericInput
            key={id + duration}
            onBlur={(duration) => {
              if (disabled || deadlineLocked) return;
              return onSubstageUpdate(substage_id, {
                name,
                date: {
                  ...date,
                  duration: duration + 'd',
                },
                time_log: {
                  ...time_log,
                  planned_time: time_log?.planned_time + ' h',
                },
              });
            }}
            readOnly={level !== 1 || disabled || deadlineLocked}
            defaultValue={duration}
          />
        );
      },
    },
    {
      key: 'schedule.elapsed_time',
      title: __('stages.schedule.elapsed_time'),
      align: 'right',
      width: 91,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.schedule?.total_elapsed_time);
      },
    },
    {
      key: 'schedule.elapsed_percent',
      title: __('stages.schedule.elapsed_percent'),
      align: 'right',
      width: 93,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.schedule?.elapsed_percent);
      },
    },
    {
      key: 'schedule.pace_gap',
      title: __('stages.schedule.pace_gap'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.schedule?.pace_gap);
      },
    },
    {
      key: 'schedule.remaining_time',
      title: __('stages.schedule.remaining_time'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.schedule?.remaining_time);
      },
    },
    {
      key: 'schedule.schedule_bar_color',
      title: __('stages.schedule.schedule_bar_color'),
      align: 'center',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const color = resolveScheduleBarColor(
          rowData?.schedule?.schedule_bar_color
        );
        const ragPace = rowData?.performance?.rag_pace || {};
        const scheduleTooltip = (
          <div className={styles.colorTooltip}>
            <div className={styles.colorTooltipRow}>
              <span>{__('stages.schedule_bar_color.tooltip.pace_gap')}</span>
              <strong>{formatPercentValue(ragPace?.pace_gap)}</strong>
            </div>
            <div className={styles.colorTooltipRow}>
              <span>
                {__('stages.schedule_bar_color.tooltip.completed_percent')}
              </span>
              <strong>{formatPercentValue(ragPace?.completed_percent)}</strong>
            </div>
            <div className={styles.colorTooltipRow}>
              <span>
                {__('stages.schedule_bar_color.tooltip.elapsed_percent')}
              </span>
              <strong>{formatPercentValue(ragPace?.elapsed_percent)}</strong>
            </div>
          </div>
        );

        if (!color) return null;

        return (
          <Tooltip text={scheduleTooltip} html>
            <div className={styles.colorDotCell}>
              <span
                className={styles.colorBall}
                style={{ backgroundColor: color }}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'time_log.planned_time',
      title: __('stages.time_log.planned_time'),
      align: 'right',
      width: 90,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const {
          status,
          id,
          level,
          date,
          name,
          description,
          time_log: { planned_time },
          time_log,
          ...otherProperties
        } = rowData;
        const disabled = status === 'canceled';

        return (
          <NumericInput
            key={id + planned_time}
            onBlur={(value) => {
              if (disabled) return;
              return onSubstageUpdate(_.last(_.split(id, '-')), {
                name,
                time_log: {
                  ...time_log,
                  planned_time: value + ' h',
                },
                date,
              });
            }}
            readOnly={level !== 1 || disabled}
            defaultValue={planned_time}
          />
        );
      },
    },
    {
      key: 'time_log.subtasks_planned_time',
      title: __('stages.flyout.toolbar.subtasks'),
      align: 'right',
      width: 90,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        if (rowData?.level !== 0) return null;
        return formatNumber(rowData?.time_log?.child_estimations_variance);
      },
    },
    {
      key: 'time_log.logged_time',
      title: __('stages.time_log.logged_time'),
      align: 'right',
      width: 94,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.time_log?.logged_time);
      },
    },
    {
      key: 'time_log.logged_percent',
      title: __('stages.time_log.logged_percent'),
      align: 'right',
      width: 95,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.time_log?.logged_percent);
      },
    },
    {
      key: 'time_log.efficiency_gap',
      title: __('stages.time_log.efficiency_gap'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.time_log?.efficiency_gap);
      },
    },
    {
      key: 'time_log.remaining_time',
      title: __('stages.time_log.remaining_time'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.time_log?.remaining_time);
      },
    },
    {
      key: 'time_log.time_log_color',
      title: __('stages.time_log.time_log_color'),
      align: 'center',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const color = resolveScheduleBarColor(
          rowData?.time_log?.time_log_color
        );
        const ragEfficiency = rowData?.performance?.rag_efficiency || {};
        const timeLogTooltip = (
          <div className={styles.colorTooltip}>
            <div className={styles.colorTooltipRow}>
              <span>{__('stages.time_log_color.tooltip.efficiency')}</span>
              <strong>{formatPercentValue(ragEfficiency?.efficiency)}</strong>
            </div>
            <div className={styles.colorTooltipRow}>
              <span>
                {__('stages.time_log_color.tooltip.completed_percent')}
              </span>
              <strong>
                {formatPercentValue(ragEfficiency?.completed_percent)}
              </strong>
            </div>
            <div className={styles.colorTooltipRow}>
              <span>{__('stages.time_log_color.tooltip.logged_percent')}</span>
              <strong>
                {formatPercentValue(ragEfficiency?.logged_percent)}
              </strong>
            </div>
          </div>
        );

        if (!color) return null;

        return (
          <Tooltip text={timeLogTooltip} html>
            <div className={styles.colorDotCell}>
              <span
                className={styles.colorBall}
                style={{ backgroundColor: color }}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'time_log.completed_progress',
      title: __('stages.time_log.completed_progress'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.time_log?.completed_progress);
      },
    },
    {
      key: 'time_log.completed_hours',
      title: __('stages.time_log.completed_hours'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.time_log?.completed_hours);
      },
    },
    {
      key: 'performance.forecast_d',
      title: __('stages.performance.forecast_d'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.performance?.forecast_d);
      },
    },
    {
      key: 'performance.forecast_h',
      title: __('stages.performance.forecast_h'),
      align: 'right',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        return formatNumber(rowData?.performance?.forecast_h);
      },
    },
    {
      key: 'performance.color',
      title: __('stages.performance.color'),
      align: 'center',
      width: 100,
      resizable: true,
      sortable: false,
      cellRenderer: ({ rowData }) => {
        const color = resolveScheduleBarColor(rowData?.performance?.color);
        const scheduleColor = resolveScheduleBarColor(
          rowData?.schedule?.schedule_bar_color
        );
        const timeLogColor = resolveScheduleBarColor(
          rowData?.time_log?.time_log_color
        );
        const performanceTooltip = (
          <div className={styles.colorTooltip}>
            <div className={styles.colorTooltipRow}>
              <span>{__('stages.performance_color.tooltip.schedule')}</span>
              <strong className={styles.colorTooltipDotValue}>
                <span
                  className={styles.colorBall}
                  style={{ backgroundColor: scheduleColor }}
                />
              </strong>
            </div>
            <div className={styles.colorTooltipRow}>
              <span>{__('stages.performance_color.tooltip.time_log')}</span>
              <strong className={styles.colorTooltipDotValue}>
                <span
                  className={styles.colorBall}
                  style={{ backgroundColor: timeLogColor }}
                />
              </strong>
            </div>
          </div>
        );

        if (!color) return null;

        return (
          <Tooltip text={performanceTooltip} html>
            <div className={styles.colorDotCell}>
              <span
                className={styles.colorBall}
                style={{ backgroundColor: color }}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      key: 'status',
      className: styles.statusCell,
      title: __('stages.columns.status'),
      overflow: true,
      cellRenderer: ({ rowData }) => {
        const {
          status,
          id,
          level,
          date,
          name,
          description,
          ...otherProperties
        } = rowData;
        const disabled = status === 'canceled';

        if (disabled || level >= 2) {
          return (
            <div
              className={classNames(styles.status, styles[_.camelCase(status)])}
              title={getLocalizedListValue(
                stage_statuses,
                status,
                'label',
                'value'
              )}
            >
              {getLocalizedListValue(stage_statuses, status, 'label', 'value')}
            </div>
          );
        }
        return (
          <Dropdown
            top={23}
            left={-100}
            wrapperClassName={styles.statusDropdownWrapper}
            // readOnly={isProjectAdministrator}
            name={'status.' + id}
            header={
              <div className={styles.statusDropdownHeader}>
                <TaskStatusIcon className={styles.statusHeaderIcon} />
                {_.startCase(__('project.stages.column.status'))}
              </div>
            }
            content={
              <div className={styles.statusDropdownContent}>
                {_.map(stage_statuses, (stage_status, i) => (
                  <div
                    key={i}
                    className={classNames(
                      styles.item,
                      status === stage_status.value && styles.active
                    )}
                    onClick={(e) => {
                      return onStatusChange(level, _.last(_.split(id, '-')), {
                        name,
                        date,
                        description,
                        status: stage_status.value,
                        ...otherProperties,
                      });
                    }}
                  >
                    <span>{stage_status.label}</span>
                    <span
                      className={styles.statusColorDot}
                      style={{ background: stage_status.color }}
                    />
                  </div>
                ))}
              </div>
            }
          >
            <div
              className={classNames(styles.status, styles[_.camelCase(status)])}
            >
              {getLocalizedListValue(stage_statuses, status, 'label', 'value')}{' '}
              <ArrowDownSmallIcon />
            </div>
          </Dropdown>
        );
      },
      width: 130,
      sortable: false,
    },

    /////////////////////////////

    // {
    //   key: 'time_log.rag',
    //   title: __('stages.time_log.rag'),
    //   align: 'right',
    //   width: 120,
    //   resizable: true,
    //   sortable: false,
    //   cellRenderer: ({ rowData }) => {
    //     return rowData?.time_log?.rag;
    //   },
    // },
    // {
    //   key: 'date.deadline',
    //   title: __('stages.deadline'),
    //   align: 'right',
    //   width: 80,
    //   resizable: true,
    //   sortable: false,
    //   cellRenderer: ({ rowData }) => {
    //     return rowData?.date?.deadline;
    //   },
    // },

    ///////////////////////////////////////////////////

    // {
    //   key: 'description',
    //   title: __('stages.description'),
    //   align: 'right',
    //   width: 160,
    //   resizable: true,
    //   sortable: false,
    //   cellRenderer: ({ rowData }) => {
    //     // if (rowData.level === 1) {
    //     //   return rowData.duration;
    //     // }
    //     return rowData?.description;
    //   },
    // },

    // {
    //   key: 'plans',
    //   sortable: false,
    //   name: __('Plans'),
    //   value: (value, row) => (
    //     <PlanSetIcon
    //       className={styles.plans}
    //       onClick={() => redirect('/stages/' + row.id + '/plans/current')}
    //     />
    //   ),
    //   width: 50,
    // },
    // {
    //   key: 'tasks',
    //   sortable: false,
    //   name: __('Tasks'),
    //   value: (value, row) => (
    //     <TasksIcon
    //       className={styles.tasks}
    //       onClick={() => redirect('/stages/' + row.id + '/tasks')}
    //     />
    //   ),
    //   width: 50,
    // },
    // {
    //   key: 'description',
    //   name: __('project.stages.column.description'),
    //   sortable: false,
    // },
    // {
    //   key: 'start_date',
    //   name: __('project.stages.column.start'),
    //   value: (value) => dateFrom(value),
    //   width: 80,
    // },
    // {
    //   key: 'end_date',
    //   name: __('project.stages.column.end'),
    //   value: (value) => dateFrom(value),
    //   width: 80,
    //   // }, {
    //   //     key: "current",
    //   //     name: __("project.stages.column.current"),
    //   //     value: current => current ? (
    //   //         <span className={classNames(styles.current, styles.active)}>{__('Yes')}</span>
    //   //     ) : (
    //   //         <span className={styles.current}>{__('No')}</span>
    //   //     ),
    //   //     width: 80,
    // },
    // {
    //   key: 'status',
    //   name: __('project.stages.column.status'),
    //   overflow: true,
    //   value: (status, row) =>
    //     this.state.is_project_administrator ? (
    //       <Dropdown
    //         top={23}
    //         right={10}
    //         name={'status.' + row.id}
    //         header={
    //           <div className={styles.statusDropdownHeader}>
    //             <StatusIcon />
    //             {__('project.stages.column.stage-status')}
    //           </div>
    //         }
    //         content={
    //           <div className={styles.statusDropdownContent}>
    //             {_.map(stage_statuses, (stage_status, i) => (
    //               <div
    //                 key={i}
    //                 className={classNames(
    //                   styles.item,
    //                   status === stage_status.value && styles.active
    //                 )}
    //                 onClick={(e) =>
    //                   this._setStageStatus(e, row.id, stage_status.value)
    //                 }
    //               >
    //                 <span>{stage_status.label}</span>
    //                 {status === stage_status.value && (
    //                   <span className={styles.dot} />
    //                 )}
    //               </div>
    //             ))}
    //           </div>
    //         }
    //       >
    //         <div
    //           className={classNames(styles.status, styles['color' + status])}
    //         >
    //           {getLocalizedListValue(
    //             stage_statuses,
    //             status,
    //             'label',
    //             'value'
    //           )}{' '}
    //           <ArrowDownSmallIcon />
    //         </div>
    //       </Dropdown>
    //     ) : (
    //       <div
    //         className={classNames(styles.status, styles['color' + status])}
    //       >
    //         {getLocalizedListValue(stage_statuses, status, 'label', 'value')}
    //       </div>
    //     ),
    //   width: 130,
    // },
  ];

  const _handleMasterCheckbox = useCallback(() => {
    if (data_ids.length == checked.length && checked.length !== 0) {
      setChecked([]);
    } else {
      setChecked(data_ids);
    }
  }, [data_ids, checked]);

  const _toggleAllColumns = useCallback(() => {
    setTempDisabledColumns((current) =>
      _.isEmpty(current)
        ? _.map(
            _.filter(columns, (column) => !column.required),
            'key'
          )
        : []
    );
  }, [columns]);

  const _resetColumnsToDefault = useCallback(() => {
    setTempDisabledColumns(
      _.map(
        _.filter(columns, (column) => column.active === false),
        'key'
      )
    );
  }, [columns]);

  const availableColumns = _.filter(columns, (column) => !column.required);
  const visibleColumns = _.filter(
    columns,
    (column) =>
      column.required || !_.includes(disabledColumns || [], column.key)
  );

  return (
    <>
      {!_.isEmpty(availableColumns) && (
        <div className={styles.tableTools}>
          <div
            className={classNames(
              styles.columnSelector,
              dropdownName === 'column_selector' && styles.active
            )}
          >
            <Dropdown
              closable={true}
              top={29}
              right={-19}
              name='column_selector'
              header={
                <div className={styles.columnSelectorHeader}>
                  {__('table.columns.select-columns')}
                </div>
              }
              content={
                <div className={styles.columnSelectorContent}>
                  {_.map(availableColumns, (column) => (
                    <div
                      onClick={() => _toggleColumn(column.key)}
                      key={column.key}
                      className={styles.column}
                    >
                      {_.upperFirst(column.title)}
                      {_.includes(tempDisabledColumns, column.key) && (
                        <UncheckedIcon className={styles.unchecked} />
                      )}
                      {!_.includes(tempDisabledColumns, column.key) && (
                        <CheckedIcon className={styles.checked} />
                      )}
                    </div>
                  ))}
                  <div
                    onClick={_resetColumnsToDefault}
                    className={styles.column}
                  >
                    {__('table.columns.reset-to-default')}
                  </div>
                  <div className={styles.saveFooter}>
                    <ButtonGroup right>
                      <Button
                        lightGray
                        medium
                        middleText={__('button.cancel')}
                        onClick={() => {
                          setTempDisabledColumns(disabledColumns || []);
                          hideDropdown();
                        }}
                      />
                      <Button
                        lightBlue
                        medium
                        middleText={__('button.done')}
                        onClick={() => {
                          onDisabledColumnsChange?.(
                            _.size(tempDisabledColumns) > 0
                              ? tempDisabledColumns
                              : null
                          );
                          hideDropdown();
                        }}
                      />
                    </ButtonGroup>
                  </div>
                </div>
              }
            >
              <Tooltip text={__('content.tooltip.table.columns')}>
                <ColumnSelectorIcon />
              </Tooltip>
            </Dropdown>
          </div>
        </div>
      )}
      <div className={styles.tableGrid}>
        <AutoSizer>
          {({ height, width }) => (
            <BaseTable
              ref={myRef}
              rowClassName={({ rowData }) =>
                classNames(
                  rowData?.level === 0 && styles.darkGray,
                  rowData?.level === 1 && styles.lightGray,
                  rowData?.level >= 2 && styles.taskGroup,
                  rowData?.status === 'canceled' && styles.disabledRow,
                  styles.row,
                  rowData?.id === focusedId && styles.focused,
                  rowData?.id === focusedId && styles.leftFloatingColumn
                )
              }
              fixed
              rowProps={({ rowData, rowIndex }) => ({
                index: rowIndex,
                id: rowData?.id,
                onClick: () => onFocusChange(rowData?.id),
                name: rowData?.name || '',
                level: rowData?.level,
                parentId: rowData?.parentId,
              })}
              components={{
                ExpandIcon,
              }}
              expandIconProps={({ rowData }) => ({
                depth: rowData?.level,
                id: rowData?.id,
              })}
              expandColumnKey='name'
              columns={visibleColumns}
              data={final_data}
              expandedRowKeys={expandedRows || []}
              width={width}
              height={height}
              onExpandedRowsChange={onExpandedRowsChange}
              ignoreFunctionInColumnCompare={false}
              headerHeight={[32, 32]}
              headerRenderer={headerRenderer}
              headerClassName={({ headerIndex }) =>
                classNames(headerIndex === 1 && styles.calculations)
              }
              rowHeight={32}
            />
          )}
        </AutoSizer>
      </div>
    </>
  );
};

export default withRouter(StagesTable);
